import { useDispatch, useSelector } from "react-redux"
import { CloseIcon } from "../../Shared/Components/SvgIcons"
import { setModalData } from "../../Shared/SharedSlices/ModalSlice"
import { RootState } from "../../../App/store"
import Button from "../../Shared/Primitive/Button"
import { deleteEventRequest, exportEventRequest } from "../RoomServiceRequests/EventServiceRequest"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import { useParams } from "react-router-dom"
import { RoleType } from "../../../App/enums"
import { setRoomEventsData } from "../RoomSlices/RoomEventsSlice"
import React, { useEffect } from "react"
import Image from "../../Shared/Primitive/Image"
import { formatDate } from "../../../App/Helpers/Helpers"
import { setRoomData } from "../../Shared/SharedSlices/RoomSlice"


function RoomEventModal() {
    const event = useSelector((state: RootState) => state.roomEventsSlice.selectedItem)
    const roomEventsData = useSelector((state: RootState) => state.roomEventsSlice)
    const roomDataState = useSelector((state: RootState) => state.roomSlice.roomSlice.room)
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const { id } = useParams()
    const dispatch = useDispatch()
    const translation = useSelector(selectTranslations)
    const [canManageEvent, setCanManageEvent] = React.useState(false)

    useEffect(() => {
        if (!event || !event.id) return;
        switch (role) {
            case RoleType.ORGANIZATION:
                setCanManageEvent(Number(id) === event.content_object.id && event.content_type === "organization")
                break;
            case RoleType.USER:
                setCanManageEvent(userDataState.id === event.content_object.id && event.content_type === "user")
                break;
            default:
                break;
        }
    }, [event])

    const onModalClose = () => {
        dispatch(setModalData({ open: false, roomEventModal: false }))
        dispatch(setRoomEventsData({ ...roomEventsData, selectedItem: undefined }))
    }

    const onDeleteButtonClick = () => {
        if (!event) return;
        const orgId = role === RoleType.ORGANIZATION ? Number(id) : undefined;
        deleteEventRequest(roomDataState.id, event.id, orgId)
            .then(() => {
                dispatch(setRoomEventsData({ ...roomEventsData, selectedItem: undefined, items: roomEventsData.items.filter((item) => item.id !== event.id) }))
                dispatch(setModalData({ open: false, roomEventModal: false }))
                dispatch(setRoomData({
                    ...roomDataState,
                    total_events: (roomDataState.total_events ?? 0) - 1
                }))
            }
            )
            .catch((error) => {
                console.error("Failed to delete event:", error)
            }
            )
    }

    const onEditButtonClick = () => {
        if (!event) return;
        const orgId = role === RoleType.ORGANIZATION ? Number(id) : undefined;
        dispatch(setModalData({ open: true, roomCreateEventModal: { edit: true } }))
        //dispatch(setModalData({ open: true, roomEventEditModal: true, eventId: event.id, orgId: orgId }))
    }

    const onDownloadIcsButtonClick = async () => {
        if (!event) return;

        try {
            const response = await exportEventRequest(event.room.id, event.id);
            const blob = new Blob([response.data], { type: "text/calendar" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${event.title || "event"}.ics`);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download ICS file:", error);
        }
    }


    return (
        <div className="event-modal">
            <div className="event-modal__content">
                <div className="event-modal__upper-panel">
                    <div className="event-modal__title">{event?.title}</div>
                    <div onClick={onModalClose} className="event-modal__close-icon">
                        <CloseIcon />
                    </div>
                </div>
                <div className="event-modal__divider"></div>
                <div className="event-modal__event-panel">
                    <div className="event-modal__image">
                        {event?.image ? (<Image src={event.image} className="event-modal__event-image" />) : (
                            <Image src="/event_default.png" className="event-modal__event-image" />
                        )}
                    </div>
                    <div className='event-modal__date-range'>
                    {event?.start_date ? formatDate(event.start_date, 'dd.MM.yyyy HH:mm') : ""}
                        {event?.end_date && event.end_date !== event.start_date && (
                            <> - {formatDate(event.end_date, 'dd.MM.yyyy HH:mm')}</>
                        )}
                    </div>
                    <div className="event-modal__description">
                        <div dangerouslySetInnerHTML={{ __html: event?.description || "" }} />
                    </div>
                </div>
                <div className="event-modal__lower-panel">
                    <div className="event-modal__tags-content">
                        <div className="event-modal__tags-content">
                            {event?.keywords?.map((keyword, index) => (
                                <div key={index} className="event-modal__tag">{keyword}</div>
                            ))}
                        </div>
                    </div>
                    {event?.location_type && (
                        <div className="event-modal__detail">
                            Veranstaltungsort: {event.location_type}
                        </div>
                    )}
                    {event?.admission && (
                        <div className="event-modal__detail">
                            Eintritt: {event.admission}
                        </div>
                    )}
                    {event?.registration_link && (
                        <div className="event-modal__detail">
                            {translation.registrationLink}: <a href={event.registration_link}>{event.registration_link}</a>
                        </div>
                    )}
                    {event?.access_link && (
                        <div className="event-modal__detail">
                            {translation.accessLink}: <a href={event.access_link}>{event.access_link}</a> 
                        </div>
                    )}
                    {event?.formats && event.formats.length > 0 && (
                        <div className="event-modal__detail">
                            Format: {event.formats}
                        </div>
                    )}
                    {event?.mail && (
                        <div className="event-modal__detail">
                            E-Mail: {event.mail}
                        </div>
                    )}
                    {event?.website && (
                        <div className="event-modal__detail">
                            Website: {event.website}
                        </div>
                    )}
                    <div className="event-modal__detail">
                        <div className="event-modal__button-container">
                            <div className='room-events-header__ics-button' onClick={onDownloadIcsButtonClick}>
                                <Button
                                    className="button button--border-radius-05 button--color-green button--hover-brighter button--cursor-pointer button--text-color-white button--font-size-small button--font-weight-400"
                                >
                                    Download
                                </Button>
                            </div>

                            <div className='room-events-header__edit-button' onClick={onEditButtonClick}>
                                <Button disabled={!canManageEvent}
                                    className="button button--border-radius-05 button--color-gold button--hover-brighter button--cursor-pointer button--text-color-white button--font-size-small button--font-weight-400"
                                >
                                    {translation.edit}
                                </Button>
                            </div>

                            <div className='room-events-header__delete-button' onClick={onDeleteButtonClick}>
                                <Button disabled={!canManageEvent}
                                    className="button button--border-radius-05 button--color-red button--hover-brighter button--cursor-pointer button--text-color-white button--font-size-small button--font-weight-400"
                                >
                                    {translation.delete}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default RoomEventModal
